/**
 * Czysta logika Lodziarni „Pieskowo Lodziarnia" — smaki, koszt, komunikaty PL, idempotencja.
 * Zakup: POST /api/economy/purchase.
 *
 * BRAK kind konsumpcyjnego w shared (tylko item|block|garden) — patrz
 * ICE_CREAM_LEDGER_* i komentarz w icecreamApi (wymagana zmiana serwera).
 */

/** Koszt jednego rożka (zł, integer). */
export const ICE_CREAM_PRICE_ZL = 5 as const;

/**
 * TEMP (P1 Terra): proxy ledgeru przez kind=block / window_round (5 zł).
 * Skutek uboczny serwera: każdy lód dopisuje blok do owned_blocks.
 * Docelowo: kind='consumable' id='icecream' (bez inventory) — zespół economy.
 */
export const ICE_CREAM_LEDGER_BLOCK_ID = 'window_round' as const;

/** Placeholder id pod przyszły kind consumable (klient gotowy na podmianę). */
export const ICE_CREAM_CONSUMABLE_ID = 'icecream' as const;

export type IceCreamFlavorId =
  | 'strawberry'
  | 'chocolate'
  | 'vanilla'
  | 'mint';

export type IceCreamFlavor = {
  id: IceCreamFlavorId;
  namePl: string;
  /** Klucz tekstury proceduralnej gałki (bez emoji-obiektów). */
  textureKey: string;
  /** Kolor gałki (Phaser 0xRRGGBB). */
  color: number;
  /** Krótka reakcja po zjedzeniu. */
  yumPl: string;
};

export const ICE_CREAM_FLAVORS: readonly IceCreamFlavor[] = [
  {
    id: 'strawberry',
    namePl: 'Truskawkowy',
    textureKey: 'icecream_flavor_strawberry',
    color: 0xff8fab,
    yumPl: 'Mniam! Truskawka!',
  },
  {
    id: 'chocolate',
    namePl: 'Czekoladowy',
    textureKey: 'icecream_flavor_chocolate',
    color: 0x8d6e63,
    yumPl: 'Pycha, czekolada!',
  },
  {
    id: 'vanilla',
    namePl: 'Waniliowy',
    textureKey: 'icecream_flavor_vanilla',
    color: 0xfff3e0,
    yumPl: 'Słodka wanilia!',
  },
  {
    id: 'mint',
    namePl: 'Miętowy',
    textureKey: 'icecream_flavor_mint',
    color: 0x81c784,
    yumPl: 'Orzeźwiająca mięta!',
  },
] as const;

/** Tekstura rożka (held item / dekor). */
export const ICE_CREAM_CONE_TEXTURE = 'icecream_cone' as const;

export function listFlavors(): readonly IceCreamFlavor[] {
  return ICE_CREAM_FLAVORS;
}

export function flavorById(id: string): IceCreamFlavor | undefined {
  return ICE_CREAM_FLAVORS.find((f) => f.id === id);
}

export function canAffordIceCream(
  balanceZl: number,
  priceZl: number = ICE_CREAM_PRICE_ZL,
): boolean {
  if (!Number.isFinite(balanceZl) || !Number.isFinite(priceZl)) return false;
  if (priceZl < 0) return false;
  return Math.floor(balanceZl) >= Math.floor(priceZl);
}

/** Naczynie: rożek (wafel) albo kubeczek. */
export type IceCreamVessel = 'cone' | 'cup';

/** Stan zamówienia (UI + potwierdzenie). */
export type IceCreamOrderState = {
  /** Gałki 1–3 (kolejność smaków). */
  scoops: IceCreamFlavorId[];
  vessel: IceCreamVessel;
  busy: boolean;
  /** UI dialogu potwierdzenia. */
  confirmOpen: boolean;
};

export const MAX_SCOOPS = 3 as const;

export const ICE_CREAM_HOLD_MS = 220;
export const ICE_CREAM_BITE_MS = 360;
export const ICE_CREAM_VESSEL_MS = 240;

export type IceCreamEatFrame = {
  phase: 'hold' | 'bite' | 'vessel' | 'done';
  remainingScoops: IceCreamFlavorId[];
  /** Indeks aktualnego ugryzienia, liczony od zjadanej górnej gałki. */
  biteIndex: number | null;
  vesselVisible: boolean;
  alpha: number;
  scale: number;
  offsetY: number;
};

export function emptyOrderState(): IceCreamOrderState {
  return { scoops: [], vessel: 'cone', busy: false, confirmOpen: false };
}

export function iceCreamEatDurationMs(scoopCount: number): number {
  const count = Number.isFinite(scoopCount)
    ? Math.max(0, Math.min(MAX_SCOOPS, Math.floor(scoopCount)))
    : 0;
  return ICE_CREAM_HOLD_MS + count * ICE_CREAM_BITE_MS + ICE_CREAM_VESSEL_MS;
}

/**
 * Deterministyczna oś jedzenia. Ostatnia gałka tablicy jest na górze i znika
 * jako pierwsza; po gałkach zostaje krótka faza miękkiego opuszczenia naczynia.
 */
export function iceCreamEatFrame(
  scoops: readonly IceCreamFlavorId[],
  _vessel: IceCreamVessel,
  elapsedMs: number,
): IceCreamEatFrame {
  const safeScoops = scoops.slice(0, MAX_SCOOPS);
  const elapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  if (elapsed < ICE_CREAM_HOLD_MS) {
    return {
      phase: 'hold',
      remainingScoops: [...safeScoops],
      biteIndex: null,
      vesselVisible: true,
      alpha: 1,
      scale: 1,
      offsetY: 0,
    };
  }

  const biteElapsed = elapsed - ICE_CREAM_HOLD_MS;
  const biteDuration = safeScoops.length * ICE_CREAM_BITE_MS;
  if (biteElapsed < biteDuration) {
    const completedBites = Math.floor(biteElapsed / ICE_CREAM_BITE_MS);
    const biteProgress =
      (biteElapsed - completedBites * ICE_CREAM_BITE_MS) /
      ICE_CREAM_BITE_MS;
    const remainingCount = Math.max(0, safeScoops.length - completedBites);
    return {
      phase: 'bite',
      remainingScoops: safeScoops.slice(0, remainingCount),
      biteIndex: completedBites,
      vesselVisible: true,
      alpha: 1,
      scale: 1 - Math.sin(biteProgress * Math.PI) * 0.1,
      offsetY: Math.sin(biteProgress * Math.PI * 2) * 2,
    };
  }

  const vesselElapsed = biteElapsed - biteDuration;
  if (vesselElapsed < ICE_CREAM_VESSEL_MS) {
    const progress = vesselElapsed / ICE_CREAM_VESSEL_MS;
    return {
      phase: 'vessel',
      remainingScoops: [],
      biteIndex: null,
      vesselVisible: true,
      alpha: 1 - progress,
      scale: 1 - progress * 0.25,
      offsetY: progress * progress * 18,
    };
  }

  return {
    phase: 'done',
    remainingScoops: [],
    biteIndex: null,
    vesselVisible: false,
    alpha: 0,
    scale: 0.75,
    offsetY: 18,
  };
}

export function heldIceCreamTextureKey(
  vessel: IceCreamVessel,
  scoops: readonly IceCreamFlavorId[],
): string {
  return `icecream_held_${vessel}_${scoops.length > 0 ? scoops.join('_') : 'empty'}`;
}

/** Cena = liczba gałek × cena za gałkę. */
export function orderPriceZl(
  state: Pick<IceCreamOrderState, 'scoops'>,
  perScoop: number = ICE_CREAM_PRICE_ZL,
): number {
  return Math.max(0, state.scoops.length) * Math.floor(perScoop);
}

/** Pierwszy smak (kompatybilność starych testów / API id). */
export function primaryFlavorId(
  state: Pick<IceCreamOrderState, 'scoops'>,
): IceCreamFlavorId | null {
  return state.scoops[0] ?? null;
}

export function selectFlavor(
  state: IceCreamOrderState,
  flavorId: IceCreamFlavorId,
): IceCreamOrderState {
  if (state.busy) return state;
  if (!flavorById(flavorId)) return state;
  // pojedynczy wybór — zamienia / startuje 1 gałkę (UI: „dołóż" dodaje)
  return { ...state, scoops: [flavorId], confirmOpen: false };
}

/** Dołóż gałkę (max 3). */
export function addScoop(
  state: IceCreamOrderState,
  flavorId: IceCreamFlavorId,
): IceCreamOrderState {
  if (state.busy) return state;
  if (!flavorById(flavorId)) return state;
  if (state.scoops.length >= MAX_SCOOPS) return state;
  return { ...state, scoops: [...state.scoops, flavorId], confirmOpen: false };
}

export function setVessel(
  state: IceCreamOrderState,
  vessel: IceCreamVessel,
): IceCreamOrderState {
  if (state.busy) return state;
  return { ...state, vessel };
}

export function openConfirm(state: IceCreamOrderState): IceCreamOrderState {
  if (state.busy || state.scoops.length === 0) return state;
  return { ...state, confirmOpen: true };
}

export function closeConfirm(state: IceCreamOrderState): IceCreamOrderState {
  return { ...state, confirmOpen: false };
}

export function clearFlavor(state: IceCreamOrderState): IceCreamOrderState {
  if (state.busy) return state;
  return { ...state, scoops: [], confirmOpen: false };
}

export function setOrderBusy(
  state: IceCreamOrderState,
  busy: boolean,
): IceCreamOrderState {
  return { ...state, busy, confirmOpen: busy ? false : state.confirmOpen };
}

export function canSubmitOrder(
  state: IceCreamOrderState,
  balanceZl: number,
  priceZl?: number,
): boolean {
  if (state.busy) return false;
  if (state.scoops.length === 0) return false;
  const price = priceZl ?? orderPriceZl(state);
  return canAffordIceCream(balanceZl, price);
}

/** Kompat: selectedFlavorId z pierwszego scoopa. */
export function getSelectedFlavorId(
  state: IceCreamOrderState,
): IceCreamFlavorId | null {
  return primaryFlavorId(state);
}

export type PurchaseFailureInput = {
  ok: false;
  code: string;
  status: number;
};

export type PurchaseSuccessInput = {
  ok: true;
  balance: number;
  flavorId: IceCreamFlavorId;
};

/**
 * Przyjazne komunikaty PL dla wyniku zakupu (wzorzec ShopScene + DoD lodziarni).
 */
export function messageForPurchaseResult(
  result: PurchaseSuccessInput | PurchaseFailureInput,
): string {
  if (result.ok) {
    const f = flavorById(result.flavorId);
    return f?.yumPl ?? 'Pyszne lody!';
  }
  if (result.code === 'INSUFFICIENT_FUNDS') {
    return 'Za mało złotówek na lody. Zagraj w mini-grę!';
  }
  if (result.code === 'UNAUTHORIZED' || result.status === 401) {
    return 'Zaloguj się ponownie, aby kupować.';
  }
  if (result.status === 0 || result.code === 'OFFLINE' || result.code === 'INTERNAL') {
    // status 0 = fetch failed (offline) — jak w ShopScene
    if (result.status === 0) {
      return 'Potrzebna sieć do zakupów. Spróbuj ponownie online.';
    }
  }
  if (result.code === 'VALIDATION' || result.code === 'NOT_FOUND') {
    return 'Spróbuj później';
  }
  return 'Spróbuj później';
}

/** Etykieta ceny przy smaku / zamówieniu. */
export function priceLabelPl(priceZl: number = ICE_CREAM_PRICE_ZL): string {
  return `${Math.floor(priceZl)} zł`;
}

export function orderSummaryPl(state: IceCreamOrderState): string {
  if (state.scoops.length === 0) return 'Wybierz smak gałki';
  const names = state.scoops
    .map((id) => flavorById(id)?.namePl ?? id)
    .join(' + ');
  const vessel = state.vessel === 'cup' ? 'kubeczek' : 'rożek';
  return `${names} · ${vessel} · ${priceLabelPl(orderPriceZl(state))}`;
}

// ── Idempotencja zakupu (stabilny klucz per intencja) ─────────────────

export type IceCreamPurchaseIntent = {
  flavorId: IceCreamFlavorId;
  idempotencyKey: string;
};

/**
 * Rozpocznij / wznów intencję zakupu.
 * Ten sam smak + aktywna intencja → ten sam klucz (retry po utraconej odpowiedzi).
 * Inny smak / brak intencji → nowy klucz.
 */
export function beginPurchaseIntent(
  flavorId: IceCreamFlavorId,
  existing: IceCreamPurchaseIntent | null,
  createKey: () => string,
): IceCreamPurchaseIntent {
  if (existing && existing.flavorId === flavorId && existing.idempotencyKey) {
    return existing;
  }
  return { flavorId, idempotencyKey: createKey() };
}

/**
 * Czy zachować intencję po odpowiedzi (retry z tym samym kluczem).
 * true tylko przy braku sieci (status 0) — serwer mógł nie zobaczyć żądania
 * albo odpowiedź zaginęła; ten sam klucz chroni przed podwójnym obciążeniem.
 * Po sukcesie / 4xx / 5xx z ciałem — nowa intencja przy kolejnym kliku.
 */
export function shouldRetainPurchaseIntent(result: {
  ok: boolean;
  status: number;
}): boolean {
  return !result.ok && result.status === 0;
}
